import React, { useEffect, useState } from "react";
import "./Dashboard.css";
import CardWrapper from "../shared/CardWrapper/CardWrapper";
import StatusTag from "../shared/StatusTag/StatusTag";
import TankLevel from "../shared/TankLevel/TankLevel";
import SenorTag from "../shared/SensorTag/SensorTag";
import BarChart from "./BarChart";
import { generateClient } from "aws-amplify/api";
import {
  getFlowProductionQuery,
  getAlertQuery,
  getStatusBarX05AndX07Query,
  getPLantPortStatusAlertQuery,
  getLatestIotDataQuery,
  getLatestTemperatureQuery,
} from "../util/query";
import Waves from "../../assests/icons/Waves";
import Pressure from "../../assests/icons/Pressure";
import { getPlantId } from "../util/helper";
const getLabel = (port) => {
  if (port === "x03") {
    return "Water flow in";
  } else if (port === "x01") {
    return "Pressure in";
  } else if (port === "x08") {
    return "Water production";
  } else if (port === "x05") {
    return "Filter 1 pressure";
  } else if (port === "x07") {
    return "Filter 2 pressure";
  } else if (port === "x04") {
    return "Water distribution";
  } else if (port === "x02") {
    return "Reject flow";
  } else if (port === "x06") {
    return "Reject pressure";
  }
  return `Sensor ${port || ""}`.trim();
};
// Custom order for ports
const portOrder = ["x03", "x01", "x05", "x07", "x08", "x04", "x02", "x06"];

// Sort function
const customSort = (a, b) => {
  const portIndexA = portOrder.indexOf(a.port);
  const portIndexB = portOrder.indexOf(b.port);
  return (portIndexA === -1 ? 999 : portIndexA) - (portIndexB === -1 ? 999 : portIndexB);
};

const isValidPlantId = (id) =>
  typeof id === "string" &&
  id.trim() !== "" &&
  id !== "undefined" &&
  id !== "null";

const hasTelemetryValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const toFixedOrDash = (value) => {
  if (!hasTelemetryValue(value)) {
    return "--";
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : "--";
};

const getTelemetryMeta = (timestamp) => {
  if (!timestamp) {
    return {
      text: "Last telemetry: no data in last 7 days",
      stale: true,
    };
  }

  const parsedDate = new Date(timestamp);
  if (Number.isNaN(parsedDate.getTime())) {
    return {
      text: `Last telemetry: ${timestamp}`,
      stale: true,
    };
  }

  const ageMs = Date.now() - parsedDate.getTime();
  const ageMinutes = Math.floor(ageMs / 60000);
  const ageHours = Math.floor(ageMinutes / 60);
  const ageDays = Math.floor(ageHours / 24);

  let ageLabel = `${ageMinutes}m ago`;
  if (ageDays > 0) {
    ageLabel = `${ageDays}d ago`;
  } else if (ageHours > 0) {
    ageLabel = `${ageHours}h ago`;
  }

  return {
    text: `Last telemetry: ${parsedDate.toLocaleString()} (${ageLabel})`,
    stale: ageMinutes > 60,
  };
};

function Dashboard({ plant_id }) {
  const candidatePlantId = getPlantId() || plant_id;
  const plantId = isValidPlantId(candidatePlantId) ? candidatePlantId : null;

  const client = generateClient();
  const [monitorData, setMonitorData] = useState(null);
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [alert07, setAlert07] = useState(null);
  const [latestTelemetryTime, setLatestTelemetryTime] = useState(null);
  const [actualTemperature, setActualTemperature] = useState(null);
  const [, setSelectedFilter] = useState("Day");
  const [, setTab] = useState("tab1");

  async function fetchFlowInProduction() {
    if (!plantId) {
      setMonitorData(null);
      return;
    }
    try {
      const query = getFlowProductionQuery(plantId);
      // Make the asynchronous GraphQL request
      const flowIn = await client.graphql({ query });
      if (flowIn && flowIn.data.getTankAndPressureData) {
        setMonitorData(flowIn.data.getTankAndPressureData);
        if (process.env.NODE_ENV !== "production") {
          console.debug("Dashboard tank/pressure response", {
            plantId,
            payload: flowIn.data.getTankAndPressureData,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching flow in production data:", error);
    }
  }

  async function fetchSensorStatus() {
    if (!plantId) {
      setDeviceStatus(null);
      return;
    }
    try {
      const query = getPLantPortStatusAlertQuery(plantId);
      // Make the asynchronous GraphQL request
      const status = await client.graphql({ query });
      const sortedDevices = status.data.getPLantPortStatusAlert
        .filter((item) => item.port !== "mo1")
        .sort(customSort);
      setDeviceStatus(sortedDevices);
    } catch (error) {
      console.error("Error fetching flow in production data:", error);
    }
  }

  async function fetchSensorStatusAlert() {
    if (!plantId) {
      setAlerts(null);
      return;
    }
    try {
      const query = getAlertQuery(plantId);
      // Make the asynchronous GraphQL request
      const status = await client.graphql({ query });
      setAlerts(status.data);
    } catch (error) {
      console.error("Error fetching flow in production data:", error);
    }
  }
  async function getStatusBarX05AndX07Alert() {
    if (!plantId) {
      setAlert07(null);
      return;
    }
    try {
      const query = getStatusBarX05AndX07Query(plantId);
      // Make the asynchronous GraphQL request
      const status = await client.graphql({ query });
      setAlert07(status.data);
    } catch (error) {
      console.error("Error fetching flow in production data:", error);
    }
  }

  async function fetchLatestTelemetryTime() {
    if (!plantId) {
      setLatestTelemetryTime(null);
      return;
    }
    try {
      const query = getLatestIotDataQuery(plantId, "week", 1);
      const response = await client.graphql({ query });
      const latest = response?.data?.getIotData?.[0]?.time || null;
      setLatestTelemetryTime(latest);
    } catch (error) {
      console.error("Error fetching latest telemetry timestamp:", error);
      setLatestTelemetryTime(null);
    }
  }

  async function fetchActualTemperature() {
    if (!plantId) {
      setActualTemperature(null);
      return;
    }
    try {
      const query = getLatestTemperatureQuery(plantId);
      const response = await client.graphql({ query });
      const latestTemperature =
        response?.data?.getIotData?.find((item) =>
          hasTelemetryValue(item?.Temperature_C)
        ) || null;
      setActualTemperature(latestTemperature);
    } catch (error) {
      console.error("Error fetching latest temperature:", error);
      setActualTemperature(null);
    }
  }

  useEffect(() => {
    const fetchData = () => {
      fetchFlowInProduction();
      fetchSensorStatus();
      fetchSensorStatusAlert();
      getStatusBarX05AndX07Alert();
      fetchLatestTelemetryTime();
      fetchActualTemperature();
    };
    fetchData(); // Initial fetch
    const interval = setInterval(fetchData, 600000); // Set interval to 10 minutes
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantId]);
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const tagColor = (alert) => {
    if (alert === "GREEN") {
      return "primary";
    } else if (alert === "RED") {
      return "error";
    } else {
      return "primary";
    }
  };

  const uniqueLabels = new Set();

  // Filter deviceStatus to include only items with unique labels
  const uniqueDeviceStatus = (deviceStatus) => {
    const uniqueLabel = deviceStatus.filter((item) => {
      const label = getLabel(item.port);
      if (uniqueLabels.has(label)) {
        return false; // Skip this item if the label is already in the set
      }
      uniqueLabels.add(label);
      return true; // Include this item if the label is unique
    });
    return uniqueLabel;
  };
  const hasTankTelemetry = hasTelemetryValue(monitorData?.currentTankLevel);
  const telemetryMeta = getTelemetryMeta(latestTelemetryTime);

  return (
    <div className="dashboard-page dashboard-page-shell mb-10">
      <div className="dashboard-meta-row">
        <div
          className={`dashboard-telemetry ${
            telemetryMeta.stale ? "is-stale" : ""
          }`}
        >
          {telemetryMeta.text}
        </div>
        {!plantId && (
          <div className="dashboard-telemetry is-stale">
            No plant is selected. Open Monitor and choose a plant to load live
            dashboard data.
          </div>
        )}
      </div>

      <div className="dashboard-layout-grid">
        <main className="dashboard-main-column">
          <BarChart
            plantId={plantId}
            setSelectedFilter={(val) => setSelectedFilter(val)}
            setTab={setTab}
          />
        </main>

        <aside className="dashboard-side-column" aria-label="Dashboard summary">
          <CardWrapper className="dashboard-temperature-card">
            <div className="dashboard-page-title">Temperature</div>
            <div className="dashboard-temperature-value">
              {toFixedOrDash(actualTemperature?.Temperature_C)}
              <span>
                {hasTelemetryValue(actualTemperature?.Temperature_C) ? " C" : ""}
              </span>
            </div>
          </CardWrapper>

          <CardWrapper className="dashboard-tank-card">
            <TankLevel
              className={"pt-5"}
              displayUnavailable={!hasTankTelemetry}
              progressValue={
                hasTankTelemetry &&
                Number.isFinite(Number(monitorData?.currentTankLevel))
                  ? Number(monitorData.currentTankLevel)
                  : 0
              }
              maxTankLevel={
                Number(monitorData?.maxTankLevel) > 0
                  ? Number(monitorData.maxTankLevel)
                  : 100
              }
            />
          </CardWrapper>
        </aside>
      </div>

      <div className="dashboard-secondary-grid">
        <CardWrapper className={"dashboard-status-card"}>
          <div className="dashboard-page-title mb-5">Status</div>
          <div className="grid grid-cols-2 gap-x-5 px-2">
            <StatusTag
              text="Intake"
              type={tagColor(alerts?.getStatusPressureIntakex01?.status_alert)}
            />
            <StatusTag
              text="Operation"
              type={tagColor(alerts?.getStatusOperation?.status_alert)}
            />
            <StatusTag
              text="Filter 1"
              type={tagColor(alerts?.getStatusBarX07AndX05?.status_alert)}
            />
            <StatusTag
              text="Filter 2"
              type={tagColor(alert07?.getStatusBarX07AndX05?.status_alert)}
            />
            <StatusTag
              text="Tank level"
              type={tagColor(
                alerts?.getStatusTankLevelBelow60Percentage?.status_alert
              )}
            />
            <StatusTag
              text="Water prod."
              type={tagColor(alerts?.getStatusBarWaterProduction?.status_alert)}
            />
          </div>
        </CardWrapper>

        <CardWrapper className={"dashboard-sensors-card"}>
          <div className="p-2">
            <div className="dashboard-page-title">Sensors</div>
            <div className="grid grid-cols-2 my-4 gap-7">
              <StatusTag text={"Connected"} type="tertiary" />
              <StatusTag text={"Disconnected"} type={"disabled"} />
            </div>
            <div className="grid grid-cols-2 gap-7 pb-3">
              {deviceStatus &&
                uniqueDeviceStatus(deviceStatus).map((item) => {
                  const label = getLabel(item.port);
                  return (
                    <SenorTag
                      key={item.port}
                      type={
                        String(item.connection_status || "").toLowerCase() ===
                        "connected"
                          ? "primary"
                          : "secondary"
                      }
                      text={label}
                      icon={label.includes("Water") ? <Waves /> : <Pressure />}
                    />
                  );
                })}
            </div>
          </div>
        </CardWrapper>
      </div>
    </div>
  );
}

export default Dashboard;
